import React, { useEffect, useMemo, useRef, useState } from 'react';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import useUserStore from '../stores/useUserStore';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import * as api from './api';
import DocSection, {
  type ArrowRef,
  DANGER_ROW_BUTTON,
  DISCLOSURE_INNER,
  HOVER_REVEAL,
  NodeEditor,
  ROW_BUTTON,
  TONAL_SMALL,
  disclosure,
  doneCopy,
  sectionDomId,
  useReturnFocus,
} from './DocSection';
import DocContents, { PROPOSED_DOM_ID, useActiveSection } from './DocContents';
import NewNodeForm from './NewNodeForm';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import { canEditNode, canEditNodeText } from './permissions';
import type { NodePatch, PlanNode, PlanResponse, TreeSection } from './types';
import { useUnread } from './useUnread';
import { getSummary } from './apiPoints';
import type { NodePointCounts, PointsHost } from './pointsTypes';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const NO_ARROWS: ArrowRef[] = [];

const bySortThenId = (a: PlanNode, b: PlanNode) =>
  a.sort !== b.sort ? a.sort - b.sort : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;

/**
 * The reading order after one container's children are put in `ordered`. Each child is
 * followed by what it holds, and the children of one container fill one contiguous run,
 * so the run is rebuilt child by child. Null when `ordered` is not such a run.
 */
const withChildrenMoved = (flat: TreeSection[], ordered: string[]): string[] | null => {
  const moved = new Set(ordered);
  const spans = new Map<string, string[]>();
  let start = -1;
  let end = -1;
  let span: string[] | null = null;
  let depth = 0;
  flat.forEach((s, i) => {
    if (moved.has(s.id)) {
      span = [s.id];
      spans.set(s.id, span);
      depth = s.depth;
      if (start < 0) start = i;
      end = i;
    } else if (span && s.depth > depth) {
      span.push(s.id);
      end = i;
    } else span = null;
  });
  const run = ordered.flatMap((id) => spans.get(id) ?? []);
  if (start < 0 || run.length !== end - start + 1) return null;
  const ids = flat.map((s) => s.id);
  return [...ids.slice(0, start), ...run, ...ids.slice(end + 1)];
};

// ── Proposed ────────────────────────────────────────────────────────────────

interface ProposedRowProps {
  node: PlanNode;
  canDecide: boolean;
  canEdit: boolean;
  onAccept: () => Promise<boolean>;
  onReject: () => Promise<void>;
  onSave: (patch: NodePatch) => Promise<boolean>;
}

const ProposedRow: React.FC<ProposedRowProps> = ({ node, canDecide, canEdit, onAccept, onReject, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const editTrigger = useReturnFocus<HTMLButtonElement>(editing);
  // A decision that succeeds reloads the plan and this row is gone before the
  // await returns, so the reset must not touch state after unmount.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!canEdit) setEditing(false);
  }, [canEdit]);

  const decide = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <li className="group np-state rounded-lg px-4 py-2 transition-colors duration-150 ease-emphasized motion-reduce:transition-none">
      {editing ? (
        <NodeEditor
          node={node}
          onCancel={() => setEditing(false)}
          onSave={async (patch) => {
            const ok = await onSave(patch);
            if (ok) setEditing(false);
            return ok;
          }}
        />
      ) : (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <div className="min-w-0 flex-1 basis-48">
            <div className="text-[14px] font-semibold leading-snug text-on-surface break-words">{node.title}</div>
            <div className="text-xs text-on-surface-variant">
              Proposed{node.proposed_by_name ? ` by ${node.proposed_by_name}` : ''}
            </div>
            {node.body && (
              <p className="mt-1 max-w-[65ch] whitespace-pre-wrap break-words text-[13px] leading-relaxed text-on-surface-variant">{node.body}</p>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {canEdit && (
              <button
                ref={editTrigger}
                type="button"
                onClick={() => setEditing(true)}
                className={`${ROW_BUTTON} ${HOVER_REVEAL}`}
                aria-label={`Edit ${node.title}`}
              >
                Edit
              </button>
            )}
            {canDecide && (
              <>
                <button type="button" disabled={busy} onClick={() => decide(onAccept)} className={TONAL_SMALL}>
                  Accept
                </button>
                <button type="button" disabled={busy} onClick={() => decide(onReject)} className={DANGER_ROW_BUTTON}>
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

// ── The document ────────────────────────────────────────────────────────────

interface DocumentViewProps {
  planId: string;
  data: PlanResponse;
  canManage: boolean;
  /** False when the plan is locked or archived: every node write would 409. */
  writable: boolean;
  reload: () => Promise<void>;
  showDone: boolean;
  onShowDone: (on: boolean) => void;
}

const DocumentView: React.FC<DocumentViewProps> = ({ planId, data, canManage, writable, reload, showDone, onShowDone }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const userId = useUserStore((s: any) => s.user?.id);
  const [contentsOpen, setContentsOpen] = useState(false);
  // Sections whose description and notes are showing. One set, so Expand all and Collapse all are one key.
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => new Set());

  const { proposed } = data.tree;
  const serverSections = data.tree.sections;
  const nodesById = useMemo(() => new Map(data.nodes.map((n) => [n.id, n])), [data.nodes]);
  const sectionsById = useMemo(() => new Map(serverSections.map((s) => [s.id, s])), [serverSections]);
  // A drop in the rail shows at once, in the rail and the body, until the refetch replaces it.
  const [dropped, setDropped] = useState<string[] | null>(null);
  // The order on screen before a saved drop. The server keeps what arrows require, so a
  // refetch that comes back in this same order means the arrows refused the move.
  const beforeDrop = useRef<string[] | null>(null);
  useEffect(() => {
    setDropped(null);
    const before = beforeDrop.current;
    if (!before) return;
    beforeDrop.current = null;
    if (before.join('|') === serverSections.map((s) => s.id).join('|')) toast.info('Arrows set that order.');
  }, [serverSections]);
  const sections = useMemo(
    () => (dropped ? dropped.map((id) => sectionsById.get(id)).filter((s): s is TreeSection => !!s) : serverSections),
    [dropped, sectionsById, serverSections],
  );
  const proposedNodes = proposed.map((id) => nodesById.get(id)).filter((n): n is PlanNode => !!n);
  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);
  const canEdit = canManage && writable;
  const [activeId, setActive] = useActiveSection(sectionIds);
  const { counts: unread, markSeen } = useUnread(planId);
  const activeUnread = activeId ? unread[activeId] ?? 0 : 0;

  // Every points write bumps the plan version, so the summary follows it.
  const version = data.plan.version;
  const [pointSummary, setPointSummary] = useState<Record<string, NodePointCounts> | null>(null);
  useEffect(() => {
    let gone = false;
    Promise.resolve()
      .then(() => getSummary(planId))
      .then((res) => {
        if (!gone) setPointSummary(res?.counts ?? {});
      })
      .catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [planId, version]);

  const pointsHost: PointsHost = {
    planId,
    role: data.role,
    userId: userId ?? null,
    locked: !!data.plan.locked_at,
    archived: !!data.plan.archived_at,
    onChanged: reload,
  };
  const members = data.members;
  const mayEditNode = (node: PlanNode) => writable && canEditNode(node, data.role, userId);

  const canContribute = data.role !== 'viewer' && writable;
  const titleOf = (id: string) => nodesById.get(id)?.title ?? '';
  const isDone = (id: string) => !!nodesById.get(id)?.done_at;
  const numberOf = (id: string) => sectionsById.get(id)?.number ?? undefined;
  // Each section's arrows in and out, ordered by where the other end sits in the document.
  // An arrow to or from a proposed topic has no section at that end and is left out.
  const arrows = useMemo(() => {
    const at = new Map(sections.map((s, i) => [s.id, i] as [string, number]));
    const from = new Map<string, ArrowRef[]>();
    const to = new Map<string, ArrowRef[]>();
    const add = (into: Map<string, ArrowRef[]>, key: string, ref: ArrowRef) => {
      const list = into.get(key);
      if (list) list.push(ref);
      else into.set(key, [ref]);
    };
    data.edges.forEach((e) => {
      if (!at.has(e.source_id) || !at.has(e.target_id)) return;
      const label = e.label.trim();
      add(from, e.target_id, { id: e.source_id, label });
      add(to, e.source_id, { id: e.target_id, label });
    });
    const byPlace = (a: ArrowRef, b: ArrowRef) => (at.get(a.id) ?? 0) - (at.get(b.id) ?? 0);
    from.forEach((list) => list.sort(byPlace));
    to.forEach((list) => list.sort(byPlace));
    return { from, to };
  }, [data.edges, sections]);
  const isEmpty = sections.length === 0 && proposedNodes.length === 0;
  // No open topic left (group headings may remain) and done topics are hidden: finished, not empty.
  const allDone = data.done_count > 0 && proposedNodes.length === 0 && sections.every((s) => s.kind === 'group');
  const allOpen = sections.length > 0 && sections.every((s) => openIds.has(s.id));
  const toggleSection = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const toggleAll = () => setOpenIds(allOpen ? new Set() : new Set(sectionIds));

  // The section being read is the one opened: its count clears, now or when the tab comes back.
  useEffect(() => {
    if (!activeId || activeUnread === 0) return undefined;
    const mark = () => {
      if (!document.hidden) markSeen(activeId);
    };
    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [activeId, activeUnread, markSeen]);

  const run = async (fn: () => Promise<unknown>, fallback: string, success?: string): Promise<boolean> => {
    try {
      await fn();
      if (success) toast.success(success);
      await reload();
      return true;
    } catch (e) {
      toast.error(api.errorMessage(e, fallback));
      reload();
      return false;
    }
  };

  const scrollTo = (domId: string) => {
    const el = document.getElementById(domId);
    if (!el) return;
    el.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    el.focus({ preventScroll: true });
    setContentsOpen(false);
  };

  const jump = (id: string) => {
    setActive(id);
    scrollTo(sectionDomId(id));
  };

  const saveNode = (id: string, patch: NodePatch) => run(() => api.updateNode(planId, id, patch), 'Could not save the topic.');

  const setDone = (id: string, done: boolean) => {
    const copy = doneCopy(done, showDone);
    return run(() => api.updateNode(planId, id, { done }), copy.fallback, copy.success);
  };

  const deleteNode = async (id: string) => {
    const isGroup = nodesById.get(id)?.kind === 'group';
    await run(
      () => api.deleteNode(planId, id),
      isGroup ? 'Could not remove the group.' : 'Could not remove the topic.',
      isGroup ? 'Group removed' : 'Topic removed',
    );
  };

  const acceptNode = (id: string) => run(() => api.acceptNode(planId, id), 'Could not accept the topic.', 'Topic accepted');

  const rejectNode = async (node: PlanNode) => {
    const ok = await confirm({
      title: 'Reject this proposal?',
      message: `"${node.title}" is removed from the plan.`,
      confirmLabel: 'Reject',
      tone: 'danger',
    });
    if (ok) await run(() => api.rejectNode(planId, node.id), 'Could not reject the topic.');
  };

  const addNode = (title: string) =>
    run(
      () => api.createNode(planId, { title }),
      canManage ? 'Could not add the topic.' : 'Could not propose the topic.',
      canManage ? undefined : 'Proposed. It waits for the owner or a plan admin.',
    );

  // The API sets sort = index over the ids it is sent, and wants every live node.
  // Proposed nodes share that sort space, so the sections are permuted inside the
  // slots they already hold and the proposals keep theirs. `ordered` is one
  // container's children; sort only ranks siblings, so the rest keep their slots.
  const reorder = async (ordered: string[]) => {
    const before = sections.map((s) => s.id);
    setDropped(withChildrenMoved(sections, ordered));
    const inGroup = new Set(ordered);
    let k = 0;
    const ids = [...data.nodes].sort(bySortThenId).map((n) => (inGroup.has(n.id) ? ordered[k++] : n.id));
    const ok = await run(async () => {
      await api.reorderNodes(planId, ids);
      beforeDrop.current = before;
    }, 'Could not save the new order.');
    if (!ok) setDropped(null);
  };

  const renderSection = (section: TreeSection) => {
    const { id } = section;
    const node = nodesById.get(id);
    if (!node) return null;
    return (
      <DocSection
        key={id}
        section={section}
        node={node}
        numberOf={numberOf}
        titleOf={titleOf}
        doneOf={isDone}
        from={arrows.from.get(id) ?? NO_ARROWS}
        to={arrows.to.get(id) ?? NO_ARROWS}
        current={id === activeId}
        canEdit={mayEditNode(node)}
        canSetAuthority={canEdit}
        members={members}
        points={pointsHost}
        pointCounts={pointSummary ? pointSummary[id] ?? null : undefined}
        open={openIds.has(id)}
        onToggle={() => toggleSection(id)}
        onSave={(patch) => saveNode(id, patch)}
        onSetDone={(done) => setDone(id, done)}
        onDelete={() => deleteNode(id)}
        onJump={jump}
      />
    );
  };

  const contents = (hideHeading: boolean) => (
    <DocContents
      sections={sections}
      titleOf={titleOf}
      isDone={isDone}
      doneCount={data.done_count}
      proposedCount={proposedNodes.length}
      activeId={activeId}
      onJump={jump}
      onJumpProposed={() => scrollTo(PROPOSED_DOM_ID)}
      hideHeading={hideHeading}
      unread={unread}
      // No grips while a drop is still showing: the next request is built from the server's
      // order, so a second drop before the refetch would undo the first without a word.
      canReorder={canEdit && !dropped}
      onReorder={reorder}
    />
  );

  const activeSection = activeId ? sectionsById.get(activeId) : undefined;

  return (
    <div className="mt-6 grid gap-6 min-[760px]:grid-cols-[220px_minmax(0,1fr)]">
      <aside className="hidden min-[760px]:block">
        <Panel
          label="Topics"
          padding="none"
          className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto supports-[height:100dvh]:max-h-[calc(100dvh-6rem)] p-2"
        >
          {contents(false)}
        </Panel>
      </aside>

      <div className="min-w-0">
        {!isEmpty && (
          <div className="mb-4 min-[760px]:hidden">
            <button
              type="button"
              aria-expanded={contentsOpen}
              aria-controls="np-contents-mobile"
              onClick={() => setContentsOpen((o) => !o)}
              className="flex min-h-[44px] w-full items-center gap-2 rounded-xl bg-surface-container-low px-4 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <span className="font-semibold text-on-surface">Contents</span>
              {activeSection && (
                <span className="min-w-0 flex-1 truncate text-right text-xs text-on-surface-variant">
                  {[activeSection.number, titleOf(activeSection.id)].filter(Boolean).join(' ')}
                </span>
              )}
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className={`h-4 w-4 flex-shrink-0 text-on-surface-variant transition-transform duration-200 ease-emphasized motion-reduce:transition-none ${
                  contentsOpen ? 'rotate-180' : ''
                } ${activeSection ? '' : 'ml-auto'}`}
                fill="none"
              >
                <path fill="currentColor" d="M16.6 8.6 12 13.2 7.4 8.6 6 10l6 6 6-6z" />
              </svg>
            </button>
            <div id="np-contents-mobile" className={disclosure(contentsOpen)} aria-hidden={!contentsOpen}>
              <div className={DISCLOSURE_INNER}>
                <div className="pt-1">
                  <Panel label="Topics" padding="none" className="p-2">{contents(true)}</Panel>
                </div>
              </div>
            </div>
          </div>
        )}

        <Reveal show={allDone && !isEmpty}>
        {allDone && !isEmpty && (
          <EmptyState
            variant="line"
            className="mb-3 justify-between rounded-panel bg-surface-container-low px-4 py-3"
            action={
              <button type="button" onClick={() => onShowDone(true)} className={ROW_BUTTON}>
                Show done
              </button>
            }
          >
            <span className="text-on-surface">Every topic is done.</span>
          </EmptyState>
        )}
        </Reveal>
        <FadeIn key={isEmpty ? (allDone ? 'done' : canContribute ? 'add' : 'none') : 'list'}>
          {isEmpty ? (
            allDone ? (
              <EmptyState
                title="Every topic is done."
                titleAs="h2"
                action={
                  <button type="button" onClick={() => onShowDone(true)} className={ROW_BUTTON}>
                    Show done
                  </button>
                }
              />
            ) : canContribute ? (
              <EmptyState title="Add the first topic" titleAs="h2">
                Each topic becomes a numbered section of this plan. Start with the one everything else hangs off.
              </EmptyState>
            ) : (
              <EmptyState title="Nothing here yet" titleAs="h2">This plan has no topics so far.</EmptyState>
            )
          ) : (
            <>
              {sections.length > 1 && (
                <div className="mb-1 flex justify-end pr-2">
                  <button type="button" onClick={toggleAll} className={ROW_BUTTON}>
                    {allOpen ? 'Collapse all' : 'Expand all'}
                  </button>
                </div>
              )}
              <div className="space-y-0.5">{sections.map(renderSection)}</div>
            </>
          )}
        </FadeIn>

        <Reveal show={proposedNodes.length > 0}>
        {proposedNodes.length > 0 && (
          <section id={PROPOSED_DOM_ID} aria-labelledby="np-proposed-h" tabIndex={-1} className="mt-8 scroll-mt-24 focus:outline-none">
            <h2 id="np-proposed-h" className="px-4 text-[18px] font-semibold text-on-surface-variant">
              Proposed
            </h2>
            <p className="px-4 text-xs text-on-surface-variant">Topics waiting for the owner or a plan admin to accept them.</p>
            <ul className="mt-2 space-y-0.5">
              {proposedNodes.map((n) => (
                <ProposedRow
                  key={n.id}
                  node={n}
                  canDecide={canEdit}
                  canEdit={writable && canEditNodeText(n, data.role, userId)}
                  onAccept={() => acceptNode(n.id)}
                  onReject={() => rejectNode(n)}
                  onSave={(patch) => saveNode(n.id, patch)}
                />
              ))}
            </ul>
          </section>
        )}
        </Reveal>

        {canContribute ? (
          <NewNodeForm canManage={canManage} onAdd={addNode} autoFocus={isEmpty} />
        ) : (
          <p className="mt-6 px-4 text-xs text-on-surface-variant">
            {data.plan.archived_at
              ? 'This plan is archived. It can be read but not changed.'
              : data.plan.locked_at
                ? 'This plan is locked. Nothing can be added or changed until it is unlocked.'
                : 'You can read this plan. Ask the owner to add you if you want to contribute.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default DocumentView;
